import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  CreateAscribeIntegrationCommand,
  CreateForstaIntegrationCommand,
  CreatePowerBIIntegrationCommand,
  IProjectIntegrationsClient,
  ProjectIntegrationsClient,
  DownloadPowerBICommand,
  CreateWalrIntegrationCommand,
  ProjectIntegrationDto,
  SorterDescriptor,
  SortDirections,
  PushForstaVisualizationIntegrationCommand,
  ForstaProjectStatuses, 
} from "app/ApiClient";
import { LoadingState } from "types";
import {
  PageContextOfProjectIntegrationFilter,
  ProjectIntegration,
  ProjectIntegrationMessage,
  initialProjectIntegrationsState,
} from "./projectIntegrationsState";
import { RootState } from "@store";
import { downloadFile } from "utils";
import { EncodingVersionDto } from "app/SharedApiClient";
import { COMMON_DATETIME_FORMAT } from "const/dateFormats";
import { projectIntegrationsTableConfig } from "const/projectIntegrations";
import { HELPDESK_LINK } from "const/config";
import { getLatestForstaProject } from "slices/forstaProject/forstaProjectSlice";
import { utcToLocalTimeZone } from "utils/timeHelper";

const projectIntegrationClient: IProjectIntegrationsClient = new ProjectIntegrationsClient(
  process.env.REACT_APP_API_URL,
);

const setEncodedVersionDate = (
  projectIntegrations: ProjectIntegrationDto[],
  availableVersions: EncodingVersionDto[],
): ProjectIntegration[] => {
  return projectIntegrations.map((item) => {
    const encodedVersionDate =
      availableVersions
        .find((i) => i.versionId === item.encodedVersionId)
        ?.versionDate.toDateFormat(COMMON_DATETIME_FORMAT) ?? "";
    return {
      ...item,
      encodedVersionDate: encodedVersionDate,
      normalizedModifiedDate: item.normalizedModifiedDate ? utcToLocalTimeZone(item.normalizedModifiedDate, COMMON_DATETIME_FORMAT) : "",
     };
  });
};

const sortIntegrations = (integrations: ProjectIntegration[], sortList?: SorterDescriptor[]): ProjectIntegration[] => {
  if (sortList && sortList.length > 0) {
    let projectIntegrations: ProjectIntegration[] = integrations;
    for (const sortDesc of sortList) {
      const sortKey = sortDesc.field as keyof ProjectIntegration;
      const sortedIntegrations = projectIntegrations.sort((a, b) => {
        const dataA = a[sortKey]?.toString();
        const dataB = b[sortKey]?.toString();

        const sortDirection: SortDirections = sortDesc.directions ?? SortDirections.Desceding;

        if (dataA && dataB) {
          return sortDirection === SortDirections.Asceding ? dataA.localeCompare(dataB) : dataB.localeCompare(dataA);
        }
        if (dataA && !dataB) {
          return sortDirection === SortDirections.Asceding ? 1 : -1;
        }
        if (!dataA && dataB) {
          return sortDirection === SortDirections.Asceding ? -1 : 1;
        }
        return 0;
      });
      projectIntegrations = sortedIntegrations;
    }
    return projectIntegrations;
  }
  return integrations;
};

export const getIntegrations = createAsyncThunk(
  "projectIntegrations/getProjectIntegrations",
  async ({ projectId, forceFetch }: { projectId: number, forceFetch?: boolean }, { getState }) => {
    const { projectIntegrations } = getState() as RootState;
    const { page: pageContext } = projectIntegrations.pageContext;

    let allData: ProjectIntegration[] = projectIntegrations.integrations.allData;
    if (!allData?.length || forceFetch) {
      const allIntegrations = await projectIntegrationClient.getByProjectId(projectId);
      const {
        projectEncodingVersion: { availableVersions },
      } = getState() as RootState;
      allData = setEncodedVersionDate(allIntegrations, availableVersions);
    }

    const {
      pageNumber = projectIntegrationsTableConfig.DefaultPageNumber,
      pageSize = projectIntegrationsTableConfig.DefaultPageSize,
    } = pageContext ?? {};

    const projectIntegrationsFilters = (projectIntegrations.filters as ProjectIntegration) ?? {};

    let filteredData: ProjectIntegration[] = [...allData];

    if (Object.keys(projectIntegrationsFilters).length > 0) {
      filteredData = filteredData.filter((item: ProjectIntegration) =>
        Object.keys(projectIntegrationsFilters).every((key) => {
          const filterValues = projectIntegrationsFilters[key as keyof ProjectIntegration];
          if (Array.isArray(filterValues) && filterValues.length > 0) {
            const fieldValue = item[key as keyof ProjectIntegration];
            const actualFieldValue = fieldValue == undefined ? "" : fieldValue;
            return filterValues.includes(actualFieldValue);
          }
          return true;
        }),
      );
    }
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    const sortedIntegrations = sortIntegrations(filteredData, projectIntegrations.pageContext.sortList);

    let result = projectIntegrations.integrations;
    result = {
      allData: allData ?? [],
      pageData: [],
      pageNumber: projectIntegrations.pageContext.page?.pageNumber,
      pageSize: projectIntegrations.pageContext.page?.pageSize,
    };

    result = {
      ...result,
      totalCount: sortedIntegrations.length,
      pageData: sortedIntegrations.slice(startIndex, endIndex),
    };
    return result;
  },
);

export const createPowerBiIntegration = createAsyncThunk(
  "projectIntegrations/createPowerBIIntegrations",
  async (data: CreatePowerBIIntegrationCommand) => {
    const result = await projectIntegrationClient.createPowerBIIntegration({ ...data });
    return result;
  },
);

export const createWalrIntegration = createAsyncThunk(
  "projectIntegrations/createWalrIntegrations",
  async (data: CreateWalrIntegrationCommand,  { dispatch }) => {
    const result = await projectIntegrationClient.createWalrIntegration({ ...data });
    dispatch(
      getIntegrations({
        projectId: data.projectId ?? 0,
        forceFetch: true
      }),
    );
    return result;
  },
);

export const createAscribeIntegration = createAsyncThunk(
  "projectIntegrations/createAscribeIntegrations",
  async (data: CreateAscribeIntegrationCommand, { dispatch }) => {
    const result = await projectIntegrationClient.createAscribeIntegration({ ...data });
    dispatch(
      getIntegrations({
        projectId: data.projectId ?? 0,
        forceFetch: true
      }),
    );
    return result;
  },
);

export const createForstaVisualizationsIntegration = createAsyncThunk(
  "projectIntegrations/createForstaVisualizationsIntegrations",
  async (data: CreateForstaIntegrationCommand, { dispatch }) => {
    const result = await projectIntegrationClient.createForstaVisualizationsIntegration({ ...data });
    dispatch(
      getIntegrations({
        projectId: data.projectId ?? 0,
        forceFetch: true
      }),
    );
    return result;
  },
);

export const pushForstaVisualizationsIntegration = createAsyncThunk(
  "projectIntegrations/pushForstaVisualizationsIntegrations",
  async (command: PushForstaVisualizationIntegrationCommand, { dispatch, rejectWithValue }) => {
    try {
      const response = await projectIntegrationClient.pushForstaVisualizations({ ...command });
      dispatch(
                  getLatestForstaProject({
                    projectId: response.projectId,
                    versionId: response.encodingVersionId,
                    forstaProjectId: response.forstaProjectId,
                    modificationId: response.modificationId,
                    stackId: response.stackId,
                  }));
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const pushForstaProjectStatus = createAsyncThunk(
  "projectIntegrations/pushForstaProjectStatus",
 async ({ forstaProjectId, forstaProjectStatus, integrationId, projectId }: { forstaProjectId: string; forstaProjectStatus:  ForstaProjectStatuses | undefined; integrationId: number; projectId?: number;}, { dispatch, rejectWithValue }) => {
     try { 
       if (forstaProjectStatus === ForstaProjectStatuses.Completed)  
       {     
          await projectIntegrationClient.updateForstaVisualizationStatus(forstaProjectId, integrationId);
          dispatch(
            getIntegrations({
              projectId: projectId ?? 0,
              forceFetch: true
            }),
          );
       }
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const downloadPowerBIFiles = createAsyncThunk(
  "projectIntegrations/downloadPowerBiFiles",
  async (command: Omit<DownloadPowerBICommand, "batteries">, { rejectWithValue, getState }) => {
    try {
      const {
        projectsData: { pageContext },
      } = getState() as RootState;
      const batteries = pageContext.filter?.batteries ?? [];

      const result = await projectIntegrationClient.downloadPowerBiFiles({ ...command, batteries });
      downloadFile(result.questionsUrl);
      downloadFile(result.answersUrl);
      downloadFile(result.demographicsUrl);
      downloadFile(result.openEndsUrl);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const refetchIntegrationIfRecordExists = createAsyncThunk( 
  "projectIntegrations/refetchIntegrationIfRecordExists", (data:{integrationId: number, projectId: number}, { dispatch , getState})=>{
    const { projectIntegrations } = getState() as RootState;
    const existingRecord  = (projectIntegrations.integrations.allData ?? []).find(x=>x.id === data.integrationId);
    if(!existingRecord)
      return;
    dispatch(getIntegrations({ projectId:data.projectId, forceFetch: true }));
  });

const projetIntegrationsSlice = createSlice({
  name: "projectIntegrations",
  initialState: initialProjectIntegrationsState,
  reducers: {
    resetIntegrations: (state) => ({
      ...state,
      initialProjectIntegrationsState,
      alertMessages : []
      }
    ), 
    resetForstaVizIntLoadingStatus: (state) => {
      state.createForstaVizIntLoadingStatus = LoadingState.idle;
    },
    resetAscribeLoadingStatus: (state) => {
      state.createAscribeLoadingStatus = LoadingState.idle;
    },
    resetPowerBiLoadingStatus :(state)=>{
      state.createPoweBIIntLoadingStatus = LoadingState.idle;
    },
    resetCreateWalrLoadingStatus :(state)=>{
      state.createWalrIntegrationLoadingStatus = LoadingState.idle;
    },
    resetPushForstaLoadingStatus: (state)=>{
      state.pushForstaIntegrationLoadingStatus = LoadingState.idle;
    },
    removeAlertMessage :(state, { payload}: {payload: string})=>{
      const filteredMessages = state.alertMessages.filter(x=>x.id !== payload);
      state.alertMessages = [...filteredMessages];
    },   
    pushIntegrationAlertMessage :(state, { payload}: PayloadAction<ProjectIntegrationMessage>)=>{
      state.alertMessages = [...state.alertMessages, {...payload}];
    },
    setProjectIntegrationsPageContext: (
      state,
      { payload }: { payload: { pageContext: PageContextOfProjectIntegrationFilter; isPageChanged?: boolean } },
    ) => {
      const isPageChanged = payload.isPageChanged;
      const pageContext = payload.pageContext;
      state.pageContext = isPageChanged ? { ...state.pageContext, page: pageContext.page } : pageContext;
      state.filters = { ...state.filters };
    },

    setIntegrationsFilters: (state, { payload }: { payload: { [key in keyof ProjectIntegration]: string[] } }) => {
      state.filters = { ...state.filters, ...payload };
    },
    
  },
  extraReducers: (builder) => {
    // getBrands
    builder.addCase(getIntegrations.pending, (state) => {
      state.loading = LoadingState.pending;
    });
    builder.addCase(getIntegrations.fulfilled, (state, { payload }) => {
      state.integrations = payload;
      state.loading = LoadingState.succeed;
    });
    builder.addCase(getIntegrations.rejected, (state) => {
      state.loading = LoadingState.failed;
    });
    builder.addCase(createPowerBiIntegration.pending, (state) => {
      state.createPoweBIIntLoadingStatus = LoadingState.pending;
    });
    builder.addCase(createPowerBiIntegration.fulfilled, (state) => {
      state.createPoweBIIntLoadingStatus = LoadingState.succeed;
    });
    builder.addCase(createPowerBiIntegration.rejected, (state) => {
      state.createPoweBIIntLoadingStatus = LoadingState.failed;
    });
    builder.addCase(createAscribeIntegration.pending, (state) => {
      state.createAscribeLoadingStatus = LoadingState.pending;
    });
    builder.addCase(createAscribeIntegration.fulfilled, (state) => {
      state.createAscribeLoadingStatus = LoadingState.succeed;
    });
    builder.addCase(createAscribeIntegration.rejected, (state) => {
      state.createAscribeLoadingStatus = LoadingState.failed;
    });
    builder.addCase(createForstaVisualizationsIntegration.pending, (state) => {
      state.createForstaVizIntLoadingStatus = LoadingState.pending;
    });
    builder.addCase(createForstaVisualizationsIntegration.fulfilled, (state) => {
      state.createForstaVizIntLoadingStatus = LoadingState.succeed;
    });
    builder.addCase(createForstaVisualizationsIntegration.rejected, (state) => {
      state.createForstaVizIntLoadingStatus = LoadingState.failed;
    });
    builder.addCase(downloadPowerBIFiles.pending, (state, { meta: {arg}})=> {
      if(!state.downloadPowerBiFileStatus)
        state.downloadPowerBiFileStatus = {}; 
     state.downloadPowerBiFileStatus[`${arg.integrationId}`] =  LoadingState.pending;
    });
    builder.addCase(downloadPowerBIFiles.fulfilled, (state, { meta:{arg}})=> {
      if(!state.downloadPowerBiFileStatus)
      state.downloadPowerBiFileStatus = {}; 
    state.downloadPowerBiFileStatus[`${arg.integrationId}`] =  LoadingState.succeed;
    });
    builder.addCase(downloadPowerBIFiles.rejected, (state, { meta: { arg}} )=> {
      if(!state.downloadPowerBiFileStatus)
      state.downloadPowerBiFileStatus = {}; 
     state.downloadPowerBiFileStatus[`${arg.integrationId}`] =  LoadingState.failed;
    });

    builder.addCase(createWalrIntegration.pending, (state, { meta: { arg: { walrDatasetName}, requestId}}) => {
      state.createWalrIntegrationLoadingStatus = LoadingState.pending; 
      state.alertMessages.push({
        message: `Walr is currently creating the dataset for <b>${walrDatasetName ?? ""}</b>. Upon completion, a new integration will be added to the table.`,
        id: requestId,
        status: LoadingState.pending,
      })
    });
    builder.addCase(createWalrIntegration.fulfilled, (state, { payload, meta: { arg: { walrDatasetName}, requestId}}) => {
      
      const alertMessages = state.alertMessages.filter(x=>x.id !== requestId);
      if(payload.integrationId !== 0)
      {
        state.createWalrIntegrationLoadingStatus = LoadingState.succeed;  
        alertMessages.push({
          message: `<b>${(walrDatasetName ?? "") }</b> has been successfully created in Walr (Walr ID <b>${(payload?.walrDatasetId ?? "")}</b>). You can now push data to Walr or open the dataset on Walr.`,
          id: requestId,
          status: LoadingState.succeed,
        });
      }
      else {
        state.createWalrIntegrationLoadingStatus = LoadingState.failed; 
        alertMessages.push({
          message: `<b>${(walrDatasetName ?? "")}</b> has failed to create in Walr. Walr has provided the following error: ${(payload?.message ?? "" )}. Please contact HELPDESK_LINK for assistance.`,
          id: requestId,
          status: LoadingState.failed,
        });
      }
      state.alertMessages=[...alertMessages];
    });
    builder.addCase(createWalrIntegration.rejected, (state, { error, meta:{ requestId, arg:{ walrDatasetName}}}) => {
      state.createWalrIntegrationLoadingStatus = LoadingState.failed;
      const alertMessages = state.alertMessages.filter(x=>x.id !== requestId);
      alertMessages.push({
        message: `<b> ${(walrDatasetName ?? "")} </b> has failed to create in Walr. Walr has provided the following error: ${(error.message ?? "") }. Please contact HELPDESK_LINK for assistance.`,
        id: requestId,
        status: LoadingState.failed,
      });
      state.alertMessages = [...alertMessages];
    });
      // exportForstaProjectByIntegration
    builder.addCase(pushForstaVisualizationsIntegration.pending, (state,  { meta:{ arg:{ integrationId}}}) => { 
      state.pushForstaIntegrationId = integrationId;
      state.pushForstaIntegrationLoadingStatus = LoadingState.pending;   
    });  
    builder.addCase(pushForstaVisualizationsIntegration.rejected,  (state, { error, meta:{ requestId, arg:{ forstaProjectId}}}) => {
      state.pushForstaIntegrationLoadingStatus = LoadingState.failed;
      state.pushForstaIntegrationId = 0;
      const alertMessages = state.alertMessages.filter(x=>x.id !== requestId);
      alertMessages.push({
        message: `Data cannot be pushed to Forsta Visualization <b> ${(forstaProjectId ?? "")} </b>. An error occurred: ${(error.message ?? "") }. Please create a PLATFORM_LINK ticket and provide the error message.`,
        id: requestId,
        status: LoadingState.failed,
        platformLink: HELPDESK_LINK, 
        platformLinkText: "Helpdesk "
      });
      state.alertMessages = [...alertMessages];
    });
     // pushForstaProjectStatus   
     builder.addCase(pushForstaProjectStatus.fulfilled,  (state) => {
      state.pushForstaIntegrationId = 0;
      state.pushForstaIntegrationLoadingStatus = LoadingState.succeed;        
    });
    builder.addCase(pushForstaProjectStatus.rejected,  (state) => {
      state.pushForstaIntegrationId = 0;
      state.pushForstaIntegrationLoadingStatus = LoadingState.failed;       
    });   
  },
});

export const { resetForstaVizIntLoadingStatus, resetAscribeLoadingStatus, resetPowerBiLoadingStatus, removeAlertMessage, resetCreateWalrLoadingStatus, resetPushForstaLoadingStatus, setIntegrationsFilters, setProjectIntegrationsPageContext, pushIntegrationAlertMessage } = projetIntegrationsSlice.actions;
export default projetIntegrationsSlice.reducer;
export const { resetIntegrations } = projetIntegrationsSlice.actions;
